import clsx from 'clsx';
import { getFinancialYearHalves, monthsEnum } from '../../../../../utils/dateUtils';
import type { MsmeDelayGraph } from '../../../../../store/dashboard/compliancesBefe/types';
import greenTick from '../../../../../assets/available.svg';
import { FY_MONTH_KEYS, MSME_EMPTY_CELL, MSME_GRID_FY_COUNT } from './MsmeDelay/constants';
import { formatMsmeAmount } from './MsmeDelay/msmeFormatters';
import styles from './scss/MsmeDelayView.module.scss';
import {
    complianceStatusUnavailable as statusUnavailable,
    complianceStatusOk as statusOk,
    complianceStatusDefault as statusDefault,
} from '../../../../../assets/hostedassets';

interface MsmeDelayViewProps {
    graphData: MsmeDelayGraph;
    isPDFReport?: boolean;
}

/**
 * MSME half-year cells: a stored amount > 0 is a delay (red ₹), 0 is no delay (green ✓), anything
 * else is unreported (grey –). A half-year that has not ended yet stays blank.
 */
function MsmeDelayView({ graphData, isPDFReport = false }: MsmeDelayViewProps) {
    const { metadata, plots } = graphData;
    const amountByMonthKey = new Map((plots?.[0]?.plotValues ?? []).map((period) => [period.xValue, period.value]));
    const rows = getFinancialYearHalves(MSME_GRID_FY_COUNT);
    const legend = [
        { img: statusUnavailable, label: metadata?.legend?.yValue3 },
        { img: statusOk, label: metadata?.legend?.yValue2 },
        { img: statusDefault, label: metadata?.legend?.yValue },
    ];

    const renderHalf = (monthKey: string, isFuture: boolean) => {
        if (isFuture) return null;
        const amount = amountByMonthKey.get(monthKey) ?? null;
        if (amount !== null && amount > 0) {
            return <div className={clsx(styles.Bar, styles.Delay)}>{formatMsmeAmount(amount)}</div>;
        }
        if (amount === 0) {
            return (
                <div className={clsx(styles.Bar, styles.NoDelay)}>
                    <img src={greenTick} alt="No Delay" height="8px" />
                </div>
            );
        }
        return <div className={clsx(styles.Bar, styles.Unavailable)}>{MSME_EMPTY_CELL}</div>;
    };

    return (
        <div className={clsx(styles.MsmeDelayView, { [styles.PDFReport]: isPDFReport })}>
            <div className={styles.Legend}>
                {legend.map(({ img, label }) => (
                    <div className={styles.LegendItem} key={label}>
                        <img src={img} alt="" height="16px" />
                        {label}
                    </div>
                ))}
            </div>
            <table className={styles.Table}>
                <thead>
                    <tr>
                        <th className={styles.FirstColumn}> </th>
                        {FY_MONTH_KEYS.map((key) => (
                            <th className={styles.MonthName} key={key}>
                                {monthsEnum[key].name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.label}>
                            <td className={styles.FyLabel}>{row.label}</td>
                            {row.halves.map(({ monthKey, isFuture }) => (
                                <td colSpan={6} className={styles.Half} key={monthKey} data-testid={`msme-half-${monthKey}`}>
                                    {renderHalf(monthKey, isFuture)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default MsmeDelayView;
